// Home v2 — Activity row card. Presentational; matches the design's `.zkx-act-row`
// (colored kind label · amount · token · counterparty / status pill · fee ·
// submitted-on · view-tx / copy-hash / repeat). Both the public and private
// activity rows feed this with their extracted fields, so the two modes look
// identical. View/Copy actions are handled here (createTab / clipboard).
import React from 'react'
import { Pressable, View } from 'react-native'

import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { isMobileWebApp } from '@common/config/env'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LinkIcon from '@common/assets/svg/LinkIcon'
import SpeedUpIcon from '@common/assets/svg/SpeedUpIcon'
import NetworkIcon from '@common/components/NetworkIcon'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import TokenIcon from '@common/components/TokenIcon'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import { setStringAsync } from '@common/utils/clipboard'
import formatDateTime from '@common/utils/formatDateTime'
import { createTab } from '@web/extension-services/background/webapi/tab'

import { ACTIVITY_KIND_COLORS, ActivityKind, RADII } from '../tokens'

export type ActivityRowData = {
  id: string
  kind: ActivityKind
  kindLabel: string
  /** Pre-formatted amount (may be signed, e.g. "+2,410"). */
  amount?: string
  tokenSymbol?: string
  tokenAddress?: string
  chainId: bigint
  counterpartyLabel?: 'from' | 'to'
  /** Already-shortened address for display. */
  counterpartyAddress?: string
  status: AccountOpStatus
  /** RAILGUN `ShieldBlocked`: shield landed on-chain but the POI list blocked
   * the commitment. NOT an on-chain failure — render an amber "Blocked" pill
   * (not red "Failed") so it reads as a POI-layer state, mirroring Railway. */
  isBlocked?: boolean
  /** PPOI.info deep link for the tx (private rows) — surfaced when `isBlocked`. */
  ppoiUrl?: string
  /** Optional pending/POI status line (private rows) — e.g. "Est. ~5 min
   * remaining for your Private Proof of Innocence". Rendered when present. */
  statusMessage?: string
  feeText?: string
  isFeeLoading?: boolean
  networkName: string
  /** Unix ms. */
  timestampMs: number
  explorerUrl?: string | null
  txHash?: string
  /** Blocked shields only: recover the funds to the origin public address. */
  onUnshieldToOrigin?: () => void
  /** Still-pending, self-broadcast txns: rebroadcast the same signed txn with a
   * bumped gas price. Surfaced inline (the detail sheet also offers it, but
   * that flow is hidden behind opening the sheet). */
  onSpeedUp?: () => void
  /** Tap the row body (everything except the action buttons) to open the
   * activity detail sheet — mirrors the legacy summary's tap-to-expand. */
  onPress?: () => void
}

const statusMeta = (status: AccountOpStatus, theme: any): { label: string; color: string } => {
  switch (status) {
    case AccountOpStatus.Success:
      return { label: 'Confirmed', color: theme.successText as string }
    case AccountOpStatus.BroadcastedButNotConfirmed:
      return { label: 'Pending', color: theme.warningText as string }
    case AccountOpStatus.PartiallyComplete:
      return { label: 'Partially completed', color: theme.warningText as string }
    case AccountOpStatus.Rejected:
      return { label: 'Failed to send', color: theme.errorText as string }
    case AccountOpStatus.Failure:
      return { label: 'Failed', color: theme.errorText as string }
    case AccountOpStatus.BroadcastButStuck:
      return { label: 'Stuck', color: theme.errorText as string }
    case AccountOpStatus.UnknownButPastNonce:
      return { label: 'Replaced (RBF)', color: theme.errorText as string }
    default:
      return { label: 'Pending', color: theme.warningText as string }
  }
}

const Action: React.FC<{ label: string; onPress: () => void; icon: React.ReactNode }> = ({
  label,
  onPress,
  icon
}) => {
  const { theme } = useTheme()
  return (
    <Pressable
      onPress={onPress}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 2 }}
    >
      <Text fontSize={12} weight="medium" appearance="secondaryText">
        {label}
      </Text>
      {icon}
    </Pressable>
  )
}

const ActivityRow: React.FC<{ data: ActivityRowData }> = ({ data }) => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { addToast } = useToast()

  const kindColor = ACTIVITY_KIND_COLORS[data.kind] || (theme.primaryText as string)
  const sm = data.isBlocked
    ? { label: 'Blocked', color: theme.warningText as string }
    : statusMeta(data.status, theme)
  const iconColor = theme.secondaryText as string

  const canViewTx = data.status !== AccountOpStatus.Rejected
  const canShowFee = canViewTx && data.status !== AccountOpStatus.BroadcastedButNotConfirmed

  const onView = () => {
    if (!data.explorerUrl || !data.txHash) {
      addToast(t('Transaction details are not available yet.'), { type: 'error' })
      return
    }
    const base = data.explorerUrl.replace(/\/$/, '')
    void createTab(`${base}/tx/${data.txHash}`)
  }
  const onCopy = async () => {
    if (!data.txHash) return
    try {
      await setStringAsync(data.txHash)
      addToast(t('Transaction hash copied to clipboard'))
    } catch {
      addToast(t('Failed to copy'), { type: 'error' })
    }
  }

  // Everything except the action buttons is one tap target that opens the detail
  // sheet. The actions row is a SIBLING (not nested) so its own Pressables don't
  // double-fire through the card tap on RNW (DOM clicks bubble, unlike RN's
  // responder system).
  const Tap: any = data.onPress ? Pressable : View

  return (
    <View
      style={{
        backgroundColor: theme.homeCardBackground as string,
        // Bordered card in the extension popup/tab only; mobile-web keeps the
        // borderless look. `isMobileWebApp` is false under chrome-extension://.
        ...(!isMobileWebApp && {
          borderWidth: 1,
          borderColor: theme.homeCardBorder as string
        }),
        borderRadius: RADII.md,
        paddingVertical: 16,
        paddingHorizontal: 20,
        gap: 14
      }}
    >
      <Tap onPress={data.onPress} style={{ gap: 14 }}>
        {/* head */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Text fontSize={16} weight="semiBold" color={kindColor}>
            {data.kindLabel}
          </Text>
          {!!data.amount && (
            <Text fontSize={15} weight="semiBold">
              {data.amount}
            </Text>
          )}
          {(!!data.tokenAddress || !!data.tokenSymbol) && (
            <TokenIcon
              width={20}
              height={20}
              chainId={data.chainId}
              address={data.tokenAddress}
              withContainer={false}
              withNetworkIcon={false}
            />
          )}
          {!!data.tokenSymbol && (
            <Text fontSize={14} weight="medium">
              {data.tokenSymbol}
            </Text>
          )}
          {!!data.counterpartyAddress && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 1 }}>
              <Text fontSize={14} appearance="secondaryText">
                {data.counterpartyLabel === 'to' ? t('to') : t('from')}
              </Text>
              <Text fontSize={13} weight="semiBold" numberOfLines={1} style={{ flexShrink: 1 }}>
                {data.counterpartyAddress}
              </Text>
            </View>
          )}
        </View>

        {/* separator */}
        <View style={{ height: 0.5, backgroundColor: theme.homeCardBorder as string }} />

        {/* pending / POI status line (private rows) — full-width, wraps */}
        {!!data.statusMessage && (
          <Text fontSize={12} weight="medium" appearance="secondaryText" numberOfLines={4}>
            {data.statusMessage}
          </Text>
        )}

        {/* meta row (status · fee · submitted-on) — Direction A: a wrapping row.
          No `flex:1` competing column, and the network line is its own non-
          shrinking unit, so the meta never word-breaks "Submitted on BNB Chain"
          or shoves onto the actions. Wraps from desktop sidebar down to a narrow
          card. Lives inside the tap target. */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 12,
            columnGap: 18
          }}
        >
          <View
            style={{
              height: 24,
              paddingHorizontal: 10,
              borderRadius: RADII.pill,
              justifyContent: 'center',
              backgroundColor: `${sm.color}22`
            }}
          >
            <Text fontSize={12} weight="semiBold" color={sm.color}>
              {sm.label}
            </Text>
          </View>

          {canShowFee && (
            <View style={{ gap: 2 }}>
              <Text fontSize={12} appearance="secondaryText">
                {t('Fee')}
              </Text>
              {data.isFeeLoading ? (
                <SkeletonLoader width={70} height={18} appearance="tertiaryBackground" />
              ) : (
                <Text fontSize={13} weight="semiBold">
                  {data.feeText || '—'}
                </Text>
              )}
            </View>
          )}

          <View style={{ gap: 2 }}>
            {/* flexShrink:0 keeps the label + network + icon on a single line. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <Text fontSize={12} appearance="secondaryText">
                {t('Submitted on')}
              </Text>
              <Text fontSize={12} weight="semiBold">
                {data.networkName}
              </Text>
              <NetworkIcon id={data.chainId.toString()} size={16} />
            </View>
            <Text fontSize={13} weight="semiBold">
              {data.timestampMs ? formatDateTime(data.timestampMs) : '—'}
            </Text>
          </View>
        </View>
      </Tap>

      {/* actions row — SIBLING of the tap target so its own buttons don't also
          trigger the card tap (open detail). RNW DOM clicks bubble, unlike RN. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 10,
          columnGap: 20,
          paddingTop: 12,
          borderTopWidth: 0.5,
          borderTopColor: theme.homeCardBorder as string
        }}
      >
        {canViewTx && (
          <Action
            label={t('View transaction')}
            onPress={onView}
            icon={<LinkIcon width={14} height={14} color={iconColor} />}
          />
        )}
        <Action
          label={t('Copy hash')}
          onPress={onCopy}
          icon={<CopyIcon width={14} height={14} color={iconColor} />}
        />
        {!!data.onSpeedUp && (
          <Pressable
            onPress={data.onSpeedUp}
            testID="activity-row-speed-up"
            style={({ hovered }: any) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              height: 28,
              paddingHorizontal: 12,
              borderRadius: RADII.pill,
              backgroundColor: `${theme.primary as string}22`,
              opacity: hovered ? 0.85 : 1
            })}
          >
            <SpeedUpIcon width={14} height={14} color={theme.primary as string} />
            <Text fontSize={12} weight="semiBold" color={theme.primary as string}>
              {t('Speed up')}
            </Text>
          </Pressable>
        )}
        {data.isBlocked && !!data.onUnshieldToOrigin && (
          <Pressable
            onPress={data.onUnshieldToOrigin}
            style={({ hovered }: any) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              height: 28,
              paddingHorizontal: 12,
              borderRadius: RADII.pill,
              backgroundColor: `${theme.warningText as string}22`,
              opacity: hovered ? 0.85 : 1
            })}
          >
            <Text fontSize={12} weight="semiBold" color={theme.warningText as string}>
              {t('Unshield to origin')}
            </Text>
          </Pressable>
        )}
        {data.isBlocked && !!data.ppoiUrl && (
          <Action
            label={t('View on PPOI.info')}
            onPress={() => void createTab(data.ppoiUrl!)}
            icon={<LinkIcon width={14} height={14} color={iconColor} />}
          />
        )}
      </View>
    </View>
  )
}

export default React.memo(ActivityRow)
